// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module MemoryAnalyticsStore
 * @package @geenius/analytics/memory
 * @description Deterministic in-process analytics provider used by tests,
 * demos, and parity checks. Data is intentionally ephemeral.
 */

import {
  analyzeCohorts,
  analyzeFunnel,
  type AnalyticsEvent,
  type AnalyticsDashboardData,
  type AnalyticsDashboardQuery,
  type AnalyticsEventQuery,
  type AnalyticsPageViewQuery,
  type AnalyticsReadResult,
  buildAnalyticsDashboardData,
  buildRealtimeStats,
  type CohortAnalysis,
  type CohortAnalysisQuery,
  type ConvexAnalyticsEvent,
  type DeviceInfo,
  type FunnelResult,
  type FunnelResultsQuery,
  type PageView,
  type RealtimeStats,
  type RealtimeStatsQuery,
  type UserTraits,
} from "@geenius/analytics";

const defaultDevice: DeviceInfo = {
  browser: "test",
  os: "memory",
  device: "desktop",
  screenWidth: 0,
  screenHeight: 0,
  language: "en",
};

/**
 * Input accepted when recording an event in the memory analytics store.
 */
export interface AnalyticsStoreEventInput {
  id?: string;
  type?: AnalyticsEvent["type"];
  name: string;
  properties?: Record<string, unknown>;
  url?: string;
  referrer?: string;
  sessionId?: string;
  userId?: string;
  device?: DeviceInfo;
  timestamp?: number;
}

/**
 * Input accepted when recording a page view in the memory analytics store.
 */
export interface AnalyticsStorePageViewInput {
  url: string;
  title?: string;
  referrer?: string;
  duration?: number;
  sessionId?: string;
  userId?: string;
  timestamp?: number;
}

/**
 * Filter options for memory-store event queries.
 */
export interface AnalyticsEventFilter {
  name?: string;
  userId?: string;
  sessionId?: string;
  start?: number;
  end?: number;
}

/**
 * Pagination request for memory-store event listings.
 */
export interface AnalyticsPaginationInput {
  limit?: number;
  cursor?: string;
  filter?: AnalyticsEventFilter;
}

/**
 * Cursor-based pagination result returned by the memory store.
 */
export interface AnalyticsPaginationResult {
  items: AnalyticsEvent[];
  nextCursor: string | null;
}

/**
 * Identity record stored by the memory analytics provider.
 */
export interface IdentifiedUser {
  userId: string;
  traits?: UserTraits;
  updatedAt: number;
}

/**
 * Public memory analytics store contract used by tests, demos, and local runtimes.
 *
 * @property setUserProperties Merges profile traits into an identified memory user.
 * @property flush Resolves immediately because memory writes are not buffered.
 * @property shutdown Clears ephemeral memory state for tracker lifecycle parity.
 * @property purgeExpiredData Evicts events, page views, and user profiles older than the cutoff.
 */
export interface MemoryAnalyticsStore {
  trackEvent(input: AnalyticsStoreEventInput): Promise<AnalyticsEvent>;
  trackPageView(input: AnalyticsStorePageViewInput): Promise<PageView>;
  identifyUser(userId: string, traits?: UserTraits): Promise<IdentifiedUser>;
  getEvent(id: string): Promise<AnalyticsEvent | null>;
  listEvents(filter?: AnalyticsEventFilter): Promise<AnalyticsEvent[]>;
  getEvents(
    query?: AnalyticsEventQuery,
  ): Promise<AnalyticsReadResult<AnalyticsEvent>>;
  getPageViews(
    query?: AnalyticsPageViewQuery,
  ): Promise<AnalyticsReadResult<PageView>>;
  getDashboardData(
    query?: AnalyticsDashboardQuery,
  ): Promise<AnalyticsDashboardData>;
  getFunnelResults(query: FunnelResultsQuery): Promise<FunnelResult>;
  getCohortAnalysis(query?: CohortAnalysisQuery): Promise<CohortAnalysis>;
  paginateEvents(
    input?: AnalyticsPaginationInput,
  ): Promise<AnalyticsPaginationResult>;
  updateEvent(
    id: string,
    patch: Partial<AnalyticsStoreEventInput>,
  ): Promise<AnalyticsEvent>;
  deleteEvent(id: string): Promise<boolean>;
  getTopPages(limit?: number): Promise<Array<{ url: string; views: number }>>;
  getRealtimeStats(query?: RealtimeStatsQuery | number): Promise<RealtimeStats>;
  setUserProperties(userId: string, traits: UserTraits): Promise<IdentifiedUser>;
  flush(): Promise<void>;
  shutdown(): Promise<void>;
  purgeExpiredData(cutoffTimestamp: number): Promise<void>;
  deleteUserData(userId: string): Promise<void>;
  clear(): Promise<void>;
}

function matchesTimeRange(
  timestamp: number,
  timeRange: { start: number; end: number } | undefined,
): boolean {
  if (!timeRange) return true;
  return timestamp >= timeRange.start && timestamp <= timeRange.end;
}

function paginate<T>(
  items: T[],
  limit: number | undefined,
  cursor: string | undefined,
): AnalyticsReadResult<T> {
  const offset = cursor ? Number.parseInt(cursor, 10) : 0;
  const safeOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;
  const safeLimit =
    typeof limit === "number" && limit > 0 ? Math.floor(limit) : items.length;
  const page = items.slice(safeOffset, safeOffset + safeLimit);
  const nextOffset = safeOffset + page.length;
  return {
    items: page,
    nextCursor: nextOffset < items.length ? String(nextOffset) : undefined,
    total: items.length,
  };
}

function matchesEventQuery(
  event: AnalyticsEvent,
  query: AnalyticsEventQuery = {},
): boolean {
  if (query.userId && event.userId !== query.userId) return false;
  if (query.sessionId && event.sessionId !== query.sessionId) return false;
  if (query.eventName && event.name !== query.eventName) return false;
  if (query.eventType && event.type !== query.eventType) return false;
  return matchesTimeRange(event.timestamp, query.timeRange);
}

function matchesPageViewQuery(
  pageView: PageView,
  query: AnalyticsPageViewQuery = {},
): boolean {
  if (query.url && pageView.url !== query.url) return false;
  if (query.userId && pageView.userId !== query.userId) return false;
  if (query.sessionId && pageView.sessionId !== query.sessionId) return false;
  return matchesTimeRange(pageView.timestamp, query.timeRange);
}

function toDashboardEvent(event: AnalyticsEvent): ConvexAnalyticsEvent {
  return {
    ...event,
    event: event.name,
  };
}

function applyCohortSegment(
  events: AnalyticsEvent[],
  segment: Record<string, unknown> | undefined,
): AnalyticsEvent[] {
  if (!segment) return events;
  return events.filter((event) =>
    Object.entries(segment).every(([key, value]) => {
      if (key === "userId") return event.userId === value;
      if (key === "sessionId") return event.sessionId === value;
      return event.properties?.[key] === value;
    }),
  );
}

/**
 * Creates a deterministic in-process analytics store.
 *
 * @returns A memory analytics store with event, page-view, identity, and aggregate helpers.
 */
export function createMemoryAnalyticsStore(): MemoryAnalyticsStore {
  const events = new Map<string, AnalyticsEvent>();
  const pageViews: PageView[] = [];
  const users = new Map<string, IdentifiedUser>();

  const matchesFilter = (
    event: AnalyticsEvent,
    filter?: AnalyticsEventFilter,
  ): boolean => {
    if (!filter) return true;
    if (filter.name && event.name !== filter.name) return false;
    if (filter.userId && event.userId !== filter.userId) return false;
    if (filter.sessionId && event.sessionId !== filter.sessionId) return false;
    if (filter.start !== undefined && event.timestamp < filter.start)
      return false;
    if (filter.end !== undefined && event.timestamp > filter.end) return false;
    return true;
  };

  const listSortedEvents = (filter?: AnalyticsEventFilter): AnalyticsEvent[] =>
    [...events.values()]
      .filter((event) => matchesFilter(event, filter))
      .sort((left, right) => left.timestamp - right.timestamp);

  const getTopPages = (limit = 10): Array<{ url: string; views: number }> => {
    const counts = new Map<string, number>();
    for (const pageView of pageViews) {
      counts.set(pageView.url, (counts.get(pageView.url) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([url, views]) => ({ url, views }))
      .sort((left, right) => right.views - left.views)
      .slice(0, limit);
  };

  return {
    async trackEvent(input) {
      const event: AnalyticsEvent = {
        id: input.id ?? `evt_${events.size + 1}`,
        type: input.type ?? "custom",
        name: input.name,
        properties: input.properties,
        url: input.url ?? "",
        referrer: input.referrer ?? "",
        sessionId: input.sessionId ?? "memory-session",
        userId: input.userId,
        device: input.device ?? defaultDevice,
        timestamp: input.timestamp ?? Date.now(),
      };
      events.set(event.id, event);
      return event;
    },
    async trackPageView(input) {
      const pageView: PageView = {
        url: input.url,
        title: input.title ?? input.url,
        referrer: input.referrer ?? "",
        duration: input.duration,
        sessionId: input.sessionId ?? "memory-session",
        userId: input.userId,
        timestamp: input.timestamp ?? Date.now(),
      };
      pageViews.push(pageView);
      return pageView;
    },
    async identifyUser(userId, traits) {
      const user = { userId, traits, updatedAt: Date.now() };
      users.set(userId, user);
      return user;
    },
    async setUserProperties(userId, traits) {
      const current = users.get(userId);
      const user = {
        userId,
        traits: {
          ...current?.traits,
          ...traits,
        },
        updatedAt: Date.now(),
      };
      users.set(userId, user);
      return user;
    },
    async getEvent(id) {
      return events.get(id) ?? null;
    },
    async listEvents(filter) {
      return listSortedEvents(filter);
    },
    async getEvents(query = {}) {
      const filtered = [...events.values()]
        .filter((event) => matchesEventQuery(event, query))
        .sort((left, right) => left.timestamp - right.timestamp);
      return paginate(filtered, query.limit, query.cursor);
    },
    async getPageViews(query = {}) {
      const filtered = [...pageViews]
        .filter((pageView) => matchesPageViewQuery(pageView, query))
        .sort((left, right) => left.timestamp - right.timestamp);
      return paginate(filtered, query.limit, query.cursor);
    },
    async getDashboardData(query = {}) {
      const pageViewResult = await this.getPageViews({
        timeRange: query.timeRange,
      });
      const eventResult = await this.getEvents({ timeRange: query.timeRange });
      const data = buildAnalyticsDashboardData({
        pageViews: pageViewResult.items,
        events: eventResult.items.map(toDashboardEvent),
      });
      return {
        ...data,
        topPages: data.topPages.slice(0, query.limit ?? data.topPages.length),
      };
    },
    async getFunnelResults(query) {
      const eventResult = await this.getEvents({
        timeRange: query.timeRange,
        limit: query.limit,
      });
      return analyzeFunnel(eventResult.items, query.steps, "Memory Funnel");
    },
    async getCohortAnalysis(query = {}) {
      const eventResult = await this.getEvents({ timeRange: query.timeRange });
      return analyzeCohorts(
        applyCohortSegment(eventResult.items, query.segment),
        {
          cohortType: query.cohortType,
          intervals: query.intervals,
          name: "Memory Cohort Analysis",
        },
      );
    },
    async paginateEvents(input = {}) {
      const limit = input.limit ?? 50;
      const offset = input.cursor ? Number.parseInt(input.cursor, 10) : 0;
      const items = listSortedEvents(input.filter).slice(
        offset,
        offset + limit,
      );
      const nextOffset = offset + items.length;
      return {
        items,
        nextCursor:
          nextOffset < listSortedEvents(input.filter).length
            ? String(nextOffset)
            : null,
      };
    },
    async updateEvent(id, patch) {
      const current = events.get(id);
      if (!current) {
        throw new Error(`Analytics event not found: ${id}`);
      }
      const updated: AnalyticsEvent = {
        ...current,
        ...patch,
        id,
        type: patch.type ?? current.type,
        name: patch.name ?? current.name,
        url: patch.url ?? current.url,
        referrer: patch.referrer ?? current.referrer,
        sessionId: patch.sessionId ?? current.sessionId,
        device: patch.device ?? current.device,
        timestamp: patch.timestamp ?? current.timestamp,
      };
      events.set(id, updated);
      return updated;
    },
    async deleteEvent(id) {
      return events.delete(id);
    },
    async getTopPages(limit = 10) {
      return getTopPages(limit);
    },
    async getRealtimeStats(query = {}) {
      const normalizedQuery =
        typeof query === "number"
          ? { now: query, windowMs: 5 * 60 * 1000 }
          : query;
      const now = normalizedQuery.now ?? Date.now();
      const windowMs =
        normalizedQuery.since !== undefined
          ? Math.max(1, now - normalizedQuery.since)
          : Math.max(1, normalizedQuery.windowMs ?? 5 * 60 * 1000);
      return buildRealtimeStats({
        events: [...events.values()].map(toDashboardEvent),
        pageViews,
        now,
        windowMs,
        topPageLimit: normalizedQuery.topPageLimit,
      });
    },
    async flush() {
      // Memory writes are synchronous, so there is no buffered work to drain.
    },
    async shutdown() {
      await this.clear();
    },
    async purgeExpiredData(cutoffTimestamp) {
      for (const [id, event] of events) {
        if (event.timestamp < cutoffTimestamp) {
          events.delete(id);
        }
      }
      for (let index = pageViews.length - 1; index >= 0; index -= 1) {
        const pageView = pageViews[index];
        if (pageView && pageView.timestamp < cutoffTimestamp) {
          pageViews.splice(index, 1);
        }
      }
      for (const [userId, user] of users) {
        if (user.updatedAt < cutoffTimestamp) {
          users.delete(userId);
        }
      }
    },
    async deleteUserData(userId) {
      for (const [id, event] of events) {
        if (event.userId === userId) {
          events.delete(id);
        }
      }
      for (let i = pageViews.length - 1; i >= 0; i--) {
        const pageView = pageViews[i];
        if (pageView?.userId === userId) {
          pageViews.splice(i, 1);
        }
      }
      users.delete(userId);
    },
    async clear() {
      events.clear();
      pageViews.splice(0, pageViews.length);
      users.clear();
    },
  };
}
